/**
 * AnnotationPowerlinesBuilder.ts - renders overhead power cables from terrain
 * marker masks. Each connected stroke becomes a sagging cable span.
 */

import { createCylinderX, createCylinderY } from "../assets/ProceduralAssets";
import {
  buildTerrainMarkerMask,
  sampleTerrainHeight,
  sampleTerrainSurface,
  type TerrainAsset,
} from "../assets/TerrainAsset";
import type { Mat4, StaticMeshPass } from "../passes/StaticMeshPass";
import type { Aabb } from "./Collisions";
import { connectedComponents8 } from "./building-extrude";

type Vec2 = [number, number];
type Vec3 = [number, number, number];

interface PowerlineConductor {
  lateralOffsetM: number;
  verticalOffsetM: number;
}

export interface PowerlineSpan {
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  length: number;
  pixelCount: number;
}

const POLE_GROUP_ID = "annotation.powerline.poles";
const CABLE_GROUP_ID = "annotation.powerline.cables";
const MIN_COMPONENT_PIXELS = 3;
const MIN_SPAN_LENGTH_M = 1.0;
const MAX_CABLE_SEGMENT_M = 2.5;
const POLYLINE_SIMPLIFY_TOLERANCE_M = 4.0;
const POLYLINE_BIN_SIZE_M = 8.0;
const MIN_POLE_SPACING_M = 120.0;

export const POWERLINE_CLEARANCE_M = 12.0;
export const POWERLINE_SAG_M = 0.75;
export const POWERLINE_COLLISION_RADIUS_M = 0.22;

const POLE_RADIUS_M = 0.12;
const POLE_EXTRA_HEIGHT_M = 0.8;
const CABLE_RADIUS_M = 0.035;
const POWERLINE_CENTER_CONDUCTOR_RISE_M = 1.0;
const POWERLINE_SIDE_CONDUCTOR_OFFSET_M = 2.0;

const POWERLINE_CONDUCTORS: PowerlineConductor[] = [
  {
    lateralOffsetM: -POWERLINE_SIDE_CONDUCTOR_OFFSET_M,
    verticalOffsetM: 0,
  },
  {
    lateralOffsetM: POWERLINE_SIDE_CONDUCTOR_OFFSET_M,
    verticalOffsetM: 0,
  },
  {
    lateralOffsetM: 0,
    verticalOffsetM: POWERLINE_CENTER_CONDUCTOR_RISE_M,
  },
];

const POLE_COLOR: [number, number, number] = [0.42, 0.38, 0.31];
const CABLE_COLOR: [number, number, number] = [0.08, 0.08, 0.07];

export class AnnotationPowerlinesBuilder {
  constructor(private readonly staticMeshPass: StaticMeshPass) {}

  build(
    terrain: TerrainAsset,
    waterLevel: number,
    staticAabbs: Aabb[],
  ): PowerlineSpan[] {
    const spans = extractPowerlinesFromAnnotation(terrain);
    if (spans.length === 0) return spans;

    this.staticMeshPass.addGroup(
      POLE_GROUP_ID,
      createCylinderY(POLE_RADIUS_M, 1, 8, POLE_COLOR, POLE_GROUP_ID, true),
      0,
      { castsShadow: true, receivesShadow: true },
    );
    this.staticMeshPass.addGroup(
      CABLE_GROUP_ID,
      createCylinderX(CABLE_RADIUS_M, 8, CABLE_COLOR, CABLE_GROUP_ID),
      0,
      { castsShadow: true, receivesShadow: true },
    );

    const polePositions: Vec2[] = [];
    for (const span of spans) {
      this.addPoleCandidate(
        terrain,
        waterLevel,
        span.x0,
        span.z0,
        polePositions,
      );
      this.addPoleCandidate(
        terrain,
        waterLevel,
        span.x1,
        span.z1,
        polePositions,
      );
      for (const segment of powerlineCableSegments(span, waterLevel)) {
        this.staticMeshPass.addInstance(
          CABLE_GROUP_ID,
          cylinderXMatrix(segment.from, segment.to),
        );
        staticAabbs.push(powerlineSegmentAabb(segment.from, segment.to));
      }
    }

    console.log(
      `[Powerlines] rendered ${spans.length} overhead cable span(s) from terrain markers`,
    );
    return spans;
  }

  private addPole(
    terrain: TerrainAsset,
    waterLevel: number,
    x: number,
    z: number,
  ): void {
    const groundY =
      waterLevel + Math.max(0, sampleTerrainHeight(terrain, x, z));
    const topY =
      waterLevel +
      POWERLINE_CLEARANCE_M +
      POWERLINE_CENTER_CONDUCTOR_RISE_M +
      POLE_EXTRA_HEIGHT_M;
    const height = Math.max(1, topY - groundY);
    this.staticMeshPass.addInstance(
      POLE_GROUP_ID,
      scaleTranslateMatrix(1, height, 1, x, groundY, z),
    );
  }

  private addPoleCandidate(
    terrain: TerrainAsset,
    waterLevel: number,
    x: number,
    z: number,
    polePositions: Vec2[],
  ): void {
    if (sampleTerrainSurface(terrain, x, z) === "water") return;
    for (const [px, pz] of polePositions) {
      if (Math.hypot(px - x, pz - z) < MIN_POLE_SPACING_M) return;
    }
    polePositions.push([x, z]);
    this.addPole(terrain, waterLevel, x, z);
  }
}

export function extractPowerlinesFromAnnotation(
  terrain: TerrainAsset,
): PowerlineSpan[] {
  const markerMask = buildTerrainMarkerMask(terrain, "powerline");
  if (!markerMask) return [];

  return connectedComponents8(
    markerMask.mask,
    markerMask.width,
    markerMask.height,
  )
    .filter((component) => component.length >= MIN_COMPONENT_PIXELS)
    .flatMap((component) =>
      powerlineSpansFromComponent(
        terrain,
        markerMask.width,
        markerMask.height,
        component,
      ),
    );
}

export function powerlineAabbs(
  span: PowerlineSpan,
  waterLevel: number,
): Aabb[] {
  return powerlineCableSegments(span, waterLevel).map((segment) =>
    powerlineSegmentAabb(segment.from, segment.to),
  );
}

function powerlineSpansFromComponent(
  terrain: TerrainAsset,
  width: number,
  height: number,
  component: Array<[number, number]>,
): PowerlineSpan[] {
  const spans = powerlineSpansFromComponentProfile(
    terrain,
    width,
    height,
    component,
  );
  if (spans.length > 0) return spans;
  const fallback = powerlineSpanFromComponentPca(
    terrain,
    width,
    height,
    component,
  );
  return fallback ? [fallback] : [];
}

function powerlineSpansFromComponentProfile(
  terrain: TerrainAsset,
  width: number,
  height: number,
  component: Array<[number, number]>,
): PowerlineSpan[] {
  if (component.length < 2) return [];
  const worldPoints = component.map(([col, row]) =>
    pixelToWorld(terrain, width, height, col, row),
  );
  const axis = pcaAxis(worldPoints);
  if (!axis) return [];
  const widthAxisX = -axis.axisZ;
  const widthAxisZ = axis.axisX;
  const projections = worldPoints.map(
    ([x, z]) => x * axis.axisX + z * axis.axisZ,
  );
  const uMin = Math.min(...projections);
  const uMax = Math.max(...projections);
  const length = uMax - uMin;
  if (length < MIN_SPAN_LENGTH_M) return [];

  const binCount = Math.max(2, Math.ceil(length / POLYLINE_BIN_SIZE_M));
  const bins = Array.from({ length: binCount }, () => ({
    count: 0,
    u: 0,
    v: 0,
  }));
  for (const [x, z] of worldPoints) {
    const u = x * axis.axisX + z * axis.axisZ;
    const v = x * widthAxisX + z * widthAxisZ;
    const bin = Math.max(
      0,
      Math.min(binCount - 1, Math.floor(((u - uMin) / length) * binCount)),
    );
    const entry = bins[bin]!;
    entry.count++;
    entry.u += u;
    entry.v += v;
  }

  const centerline: Vec2[] = [];
  for (const bin of bins) {
    if (bin.count === 0) continue;
    const u = bin.u / bin.count;
    const v = bin.v / bin.count;
    centerline.push([
      axis.axisX * u + widthAxisX * v,
      axis.axisZ * u + widthAxisZ * v,
    ]);
  }
  const points = simplifyPolyline(centerline, POLYLINE_SIMPLIFY_TOLERANCE_M);
  const spans: PowerlineSpan[] = [];
  for (let i = 1; i < points.length; i++) {
    const [x0, z0] = points[i - 1]!;
    const [x1, z1] = points[i]!;
    const length = Math.hypot(x1 - x0, z1 - z0);
    if (length < MIN_SPAN_LENGTH_M) continue;
    spans.push({
      x0,
      z0,
      x1,
      z1,
      length,
      pixelCount: component.length,
    });
  }
  return spans;
}

function pixelToWorld(
  terrain: TerrainAsset,
  width: number,
  height: number,
  col: number,
  row: number,
): Vec2 {
  const pxToWorldX = terrain.worldWidthM / width;
  const pxToWorldZ = terrain.worldHeightM / height;
  return [
    -terrain.worldWidthM * 0.5 + (col + 0.5) * pxToWorldX,
    -terrain.worldHeightM * 0.5 + (row + 0.5) * pxToWorldZ,
  ];
}

function simplifyPolyline(points: Vec2[], tolerance: number): Vec2[] {
  if (points.length <= 2) return points;
  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  simplifyPolylineRange(points, 0, points.length - 1, tolerance, keep);
  return points.filter((_, index) => keep[index] === 1);
}

function simplifyPolylineRange(
  points: Vec2[],
  start: number,
  end: number,
  tolerance: number,
  keep: Uint8Array,
): void {
  if (end <= start + 1) return;
  const a = points[start]!;
  const b = points[end]!;
  let maxDistance = -1;
  let maxIndex = -1;
  for (let i = start + 1; i < end; i++) {
    const distance = pointLineDistance(points[i]!, a, b);
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }
  if (maxDistance <= tolerance || maxIndex < 0) return;
  keep[maxIndex] = 1;
  simplifyPolylineRange(points, start, maxIndex, tolerance, keep);
  simplifyPolylineRange(points, maxIndex, end, tolerance, keep);
}

function pointLineDistance(point: Vec2, a: Vec2, b: Vec2): number {
  const abX = b[0] - a[0];
  const abZ = b[1] - a[1];
  const lenSq = abX * abX + abZ * abZ;
  if (lenSq <= 1e-9) return Math.hypot(point[0] - a[0], point[1] - a[1]);
  const t = Math.max(
    0,
    Math.min(1, ((point[0] - a[0]) * abX + (point[1] - a[1]) * abZ) / lenSq),
  );
  const projX = a[0] + abX * t;
  const projZ = a[1] + abZ * t;
  return Math.hypot(point[0] - projX, point[1] - projZ);
}

function powerlineSpanFromComponentPca(
  terrain: TerrainAsset,
  width: number,
  height: number,
  component: Array<[number, number]>,
): PowerlineSpan | null {
  const worldWidth = terrain.worldWidthM;
  const worldHeight = terrain.worldHeightM;
  const pxToWorldX = worldWidth / width;
  const pxToWorldZ = worldHeight / height;
  const halfWorldWidth = worldWidth * 0.5;
  const halfWorldHeight = worldHeight * 0.5;
  const cellM = Math.max(pxToWorldX, pxToWorldZ);
  const points: Vec2[] = component.map(([col, row]) => [
    -halfWorldWidth + (col + 0.5) * pxToWorldX,
    -halfWorldHeight + (row + 0.5) * pxToWorldZ,
  ]);
  const pca = pcaAxis(points);
  if (!pca) return null;

  const spanExtents = projectedExtents(points, pca.axisX, pca.axisZ);
  const widthAxisX = -pca.axisZ;
  const widthAxisZ = pca.axisX;
  const widthExtents = projectedExtents(points, widthAxisX, widthAxisZ);
  if (!spanExtents || !widthExtents) return null;

  const length = spanExtents.max - spanExtents.min;
  if (length < Math.max(MIN_SPAN_LENGTH_M, cellM * 1.5)) return null;
  const widthMid = (widthExtents.min + widthExtents.max) * 0.5;
  const x0 = pca.axisX * spanExtents.min + widthAxisX * widthMid;
  const z0 = pca.axisZ * spanExtents.min + widthAxisZ * widthMid;
  const x1 = pca.axisX * spanExtents.max + widthAxisX * widthMid;
  const z1 = pca.axisZ * spanExtents.max + widthAxisZ * widthMid;

  return {
    x0,
    z0,
    x1,
    z1,
    length,
    pixelCount: component.length,
  };
}

function powerlineCableSegments(
  span: PowerlineSpan,
  waterLevel: number,
): Array<{ from: Vec3; to: Vec3 }> {
  const segmentCount = Math.max(
    1,
    Math.ceil(span.length / MAX_CABLE_SEGMENT_M),
  );
  const segments: Array<{ from: Vec3; to: Vec3 }> = [];
  for (const conductor of POWERLINE_CONDUCTORS) {
    for (let i = 0; i < segmentCount; i++) {
      const t0 = i / segmentCount;
      const t1 = (i + 1) / segmentCount;
      segments.push({
        from: powerlinePoint(span, waterLevel, t0, conductor),
        to: powerlinePoint(span, waterLevel, t1, conductor),
      });
    }
  }
  return segments;
}

function powerlinePoint(
  span: PowerlineSpan,
  waterLevel: number,
  t: number,
  conductor: PowerlineConductor,
): Vec3 {
  const invT = 1 - t;
  const dx = span.x1 - span.x0;
  const dz = span.z1 - span.z0;
  const length = Math.hypot(dx, dz);
  const lateralX = length > 1e-6 ? -dz / length : 0;
  const lateralZ = length > 1e-6 ? dx / length : 0;
  const sag = POWERLINE_SAG_M * 4 * t * invT;
  return [
    span.x0 * invT + span.x1 * t + lateralX * conductor.lateralOffsetM,
    waterLevel + POWERLINE_CLEARANCE_M + conductor.verticalOffsetM - sag,
    span.z0 * invT + span.z1 * t + lateralZ * conductor.lateralOffsetM,
  ];
}

function powerlineSegmentAabb(from: Vec3, to: Vec3): Aabb {
  const radius = POWERLINE_COLLISION_RADIUS_M;
  return {
    min: [
      Math.min(from[0], to[0]) - radius,
      Math.min(from[1], to[1]) - radius,
      Math.min(from[2], to[2]) - radius,
    ],
    max: [
      Math.max(from[0], to[0]) + radius,
      Math.max(from[1], to[1]) + radius,
      Math.max(from[2], to[2]) + radius,
    ],
    kind: "powerline",
  };
}

function pcaAxis(points: ReadonlyArray<Vec2>): {
  axisX: number;
  axisZ: number;
} | null {
  if (points.length < 2) return null;
  let centerX = 0;
  let centerZ = 0;
  for (const [x, z] of points) {
    centerX += x;
    centerZ += z;
  }
  centerX /= points.length;
  centerZ /= points.length;

  let covXX = 0;
  let covXZ = 0;
  let covZZ = 0;
  for (const [x, z] of points) {
    const dx = x - centerX;
    const dz = z - centerZ;
    covXX += dx * dx;
    covXZ += dx * dz;
    covZZ += dz * dz;
  }
  const invCount = 1 / points.length;
  covXX *= invCount;
  covXZ *= invCount;
  covZZ *= invCount;

  const trace = covXX + covZZ;
  const determinant = covXX * covZZ - covXZ * covXZ;
  const discriminant = Math.max(0, trace * trace * 0.25 - determinant);
  const eigenvalue = trace * 0.5 + Math.sqrt(discriminant);
  let axisX = covXZ;
  let axisZ = eigenvalue - covXX;
  if (Math.abs(axisX) < 1e-9 && Math.abs(axisZ) < 1e-9) {
    axisX = 1;
    axisZ = 0;
  }
  const len = Math.hypot(axisX, axisZ);
  if (len < 1e-6) return null;
  return { axisX: axisX / len, axisZ: axisZ / len };
}

function projectedExtents(
  points: ReadonlyArray<Vec2>,
  axisX: number,
  axisZ: number,
): { min: number; max: number } | null {
  if (points.length === 0) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const [x, z] of points) {
    const value = x * axisX + z * axisZ;
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
}

function scaleTranslateMatrix(
  sx: number,
  sy: number,
  sz: number,
  tx: number,
  ty: number,
  tz: number,
): Mat4 {
  return new Float32Array([
    sx,
    0,
    0,
    0,
    0,
    sy,
    0,
    0,
    0,
    0,
    sz,
    0,
    tx,
    ty,
    tz,
    1,
  ]) as Mat4;
}

function cylinderXMatrix(from: Vec3, to: Vec3): Mat4 {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const dz = to[2] - from[2];
  const len = Math.hypot(dx, dy, dz);
  const dir: Vec3 = len > 1e-6 ? [dx / len, dy / len, dz / len] : [1, 0, 0];
  const aux: Vec3 = Math.abs(dir[1]) < 0.9 ? [0, 1, 0] : [0, 0, 1];
  const yAxis = normalize(cross(aux, dir));
  const zAxis = cross(dir, yAxis);
  const mid: Vec3 = [
    (from[0] + to[0]) * 0.5,
    (from[1] + to[1]) * 0.5,
    (from[2] + to[2]) * 0.5,
  ];

  return new Float32Array([
    dir[0] * len,
    dir[1] * len,
    dir[2] * len,
    0,
    yAxis[0],
    yAxis[1],
    yAxis[2],
    0,
    zAxis[0],
    zAxis[1],
    zAxis[2],
    0,
    mid[0],
    mid[1],
    mid[2],
    1,
  ]) as Mat4;
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function normalize(v: Vec3): Vec3 {
  const len = Math.hypot(v[0], v[1], v[2]);
  if (len < 1e-6) return [0, 1, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}
